import { useCallback, useState, useRef } from 'react';

import { useSort } from './useSort';
import { useFetch } from './useFetch';
import { useParam } from './useParam';
import { usePaginated } from './usePaginated';
import { useUpdateEffect } from './useUpdateEffect';
import { useDebouncedValue } from './useDebouncedValue';
import type { UseFetchConfig } from './useFetch';
import type { Pagination, UsePaginatedResult } from './usePaginated';

const DEFAULT_PAGE_SIZE = 1_000;

export type UseListFetchConfig<T, FiltersType = Record<any, any>> = {
  initialPage?: number;
  initialSearch?: string;
  initialPageSize?: number;
  initialSort?: SortState<string>;
  initialFilters?: FiltersType;

  resultKey?: string;
  settingsKey?: string;
  withQueryParams?: boolean;
  additionalParams?: Record<string, string>;
  deps?: any[];

  skip?: boolean;
  loadMore?: boolean;

  storageKey?: string;
  storageType?: 'session' | 'local';
} & UseFetchConfig<T>;

export type WithPaginationResult<T> = {
  total?: number;
  totalElements?: number;
  content?: T[];
};

export type ListParamsType<FiltersType = Record<any, any>> = {
  search?: string;
  sortStr?: string | null;
  sort?: SortState<string>;
  filters?: FiltersType;
  pagination: Pagination;

  additionalParams?: Record<string, string>;
};

export type ListFetchRequestType<T, FiltersType = Record<any, any>> = (
  params: ListParamsType<FiltersType>,
  cancelToken?: AbortSignal
) => Promise<WithPaginationResult<T>> | WithPaginationResult<T>;

export type UseListFetchResult<T, FiltersType = Record<any, any>> = {
  nodes: T[];
  loading: boolean;
  initializing: boolean;
  reloading: boolean;
  error: Error | null;
  refresh: () => void;
  rawData: any;
  search: string;

  sort?: SortState<string>;
  filters?: FiltersType;
  hasFilters: boolean;
  filtersKey: string;

  setSort: (sort: string) => void;
  setSearch: (search: string) => void;

  setFilters: (filters: FiltersType) => void;
  resetFilters: () => void;
} & UsePaginatedResult<T>;

const getNodes = <T>(data: any, key: string): T[] => {
  return key ? (data as any)?.[key] : (data?.content ?? []);
};

const toSortParam = (sort?: SortState<string>): string | null => {
  if (!sort?.name) return null;
  return `${sort.name}:${sort.direction || 'asc'}`;
};

const useListSort = <FiltersType>(config?: UseListFetchConfig<FiltersType>) => {
  const storageType = config?.storageType;
  const storageKey = config?.storageKey
    ? `${config?.storageKey}.sort`
    : undefined;

  const [urlSort, setUrlSort] = useParam({
    name: 'sort',
    enableUrl: !!config?.withQueryParams,
    defaultValue: config?.initialSort ?? undefined,

    storageType,
    storageKey,
  });

  const { sort, setSort: toggleSort } = useSort({
    defaultValue: urlSort,
    useUrl: false,
  });

  const handleChange = useCallback(
    (newSort: string) => {
      toggleSort(newSort);
    },
    [toggleSort]
  );

  useUpdateEffect(
    () => {
      setUrlSort(sort?.name ? sort : undefined);
    },
    [sort],
    true
  );

  return [sort, handleChange] as const;
};

const hasActiveFilters = <T extends Record<any, unknown> | undefined>(
  filters?: T
) => {
  return Object.values(filters ?? {}).some(
    value => value !== '' && value !== undefined && value !== null
  );
};

export const useListFetch = <
  T,
  FiltersType extends Record<any, any> | undefined = Record<any, any>,
>(
  request: ListFetchRequestType<T, FiltersType>,
  config?: UseListFetchConfig<T, FiltersType>
): UseListFetchResult<T, FiltersType> => {
  const [filtersKey, setFiltersKey] = useState(1);
  const { pagination, setPage, ...pagingProps } = usePaginated<T>({
    ...config,
    storageType: config?.storageType,
    storageKey: config?.storageKey
      ? `${config?.storageKey}.pagination`
      : undefined,
    initialPageSize: config?.initialPageSize || DEFAULT_PAGE_SIZE,
  });

  const loadMoreRef = useRef(false);
  const [sort, setSort] = useListSort(config);
  const [localSearch, setSearch] = useState(config?.initialSearch ?? '');
  const search = useDebouncedValue(localSearch, 500);
  const [filters, setFilters] = useParam<FiltersType>({
    name: 'filters',
    enableUrl: !!config?.withQueryParams,
    defaultValue: (config?.initialFilters ?? {}) as FiltersType,

    storageType: config?.storageType,
    storageKey: config?.storageKey
      ? `${config?.storageKey}.filters`
      : undefined,
  });
  const setNonEmptyFilters = useCallback<typeof setFilters>(
    filters => {
      const nonEmpty = Object.entries(filters || {}).filter(
        ([_k, val]) => val !== '' && val !== undefined
      );
      if (!nonEmpty.length) return setFilters(undefined);

      setFilters(Object.fromEntries(nonEmpty) as FiltersType);
    },
    [setFilters]
  );
  const loadMore = config?.loadMore;
  const resultKey = config?.resultKey || '';

  const params: ListParamsType<FiltersType> = {
    sort,
    search,
    filters,
    pagination: pagination,
    sortStr: toSortParam(sort),
    additionalParams: config?.additionalParams,
  };

  const { data, refresh, ...rest } = useFetch(
    token => request?.(params, token),
    [
      pagination.page,
      pagination.perPage,
      search,
      sort,
      filters,
      loadMore,
      config?.additionalParams,
      ...(config?.deps ?? []),
    ],
    {
      delay: 250,
      skip: config?.skip,
      transformData: (newData, old): any => {
        if (!loadMore || !loadMoreRef.current) {
          return newData || null;
        }

        loadMoreRef.current = false;

        const newNodes = getNodes<T>(newData, resultKey);
        const oldNodes = getNodes<T>(old, resultKey);

        return {
          ...old,
          nodes: [...oldNodes, ...newNodes],
        };
      },
    }
  );

  const handleSetSearch: UseListFetchResult<T>['setSearch'] = (...params) => {
    setPage(1);
    setSearch(...params);
  };

  const handleSetSort: UseListFetchResult<T>['setSort'] = (...params) => {
    setPage(1);
    setSort(...params);
  };

  const handleSetFilters: UseListFetchResult<T, FiltersType>['setFilters'] = (
    ...params
  ) => {
    setPage(1);
    setNonEmptyFilters(...params);
  };
  const handleResetFilters = useCallback(() => {
    setFilters({} as FiltersType);
    setFiltersKey(k => k + 1);
  }, [setFilters, setFiltersKey]);
  const handleRefresh = useCallback(
    () => {
      if (!loadMore) return refresh?.();

      setPage(1);
      refresh?.();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadMore, refresh]
  );

  const totalItems = data?.total ?? data?.totalElements ?? 0;
  const hasFilters = hasActiveFilters<FiltersType>(filters);

  return {
    ...pagingProps,
    nodes: getNodes<T>(data, resultKey),

    setPage,
    setSort: handleSetSort,
    setSearch: handleSetSearch,
    setFilters: handleSetFilters,
    resetFilters: handleResetFilters,

    rawData: data,
    sort,
    filters,
    hasFilters,
    filtersKey: String(filtersKey),
    search: localSearch,
    pagination: {
      ...pagination,
      totalItems,
      totalPages: Math.ceil(totalItems / (pagination.perPage || 1)),
    },
    refresh: handleRefresh,

    ...rest,
  };
};
